//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import { FieldError } from 'react-hook-form';
import styled, { css } from 'styled-components';

import { TypeaheadSearchFieldLabel } from '@campaigns/components/shared/product-details-modal/form/TypeaheadSearchFieldLabel';
import Icon from '@components/atoms/icon/Icon';
import { CaptionErrorText } from '@components/atoms/text/Text';
import DropdownButton from '@components/molecules/button-popover/DropdownButton';
import { DropdownMenu } from '@components/primitives/dropdown-menu/DropdownMenu';
import { TrackingIdPrefix } from '@models/TrackingId';
import { isDefined, isTruthy } from '@utils/filterPredicates';

interface TypeaheadSearchOption {
  externalID: string;
  name: string;
}

interface TypeaheadSearchFieldProps {
  debouncedSearchTerm: string;
  error?: FieldError;
  fieldId: string;
  fieldLabelId: string;
  helpText: string;
  isLoading: boolean;
  labelText: string;
  onSearch: (searchTerm: string) => void;
  onSelect: (value: string) => void;
  options: TypeaheadSearchOption[];
  placeholderText: string;
  queryError: Error | null;
  resourceName: string;
  searchKeys?: string[];
  searchPlaceholder: string;
  trackingIdPrefix: TrackingIdPrefix;
  value?: string;
  visuallyHiddenLabel: string;
}

export const TypeaheadSearchField = ({
  debouncedSearchTerm,
  error,
  fieldId,
  fieldLabelId,
  helpText,
  isLoading,
  labelText,
  onSearch,
  onSelect,
  options,
  placeholderText,
  queryError,
  resourceName,
  searchKeys = ['name'],
  searchPlaceholder,
  trackingIdPrefix,
  value = '',
  visuallyHiddenLabel,
}: TypeaheadSearchFieldProps) => {
  return (
    <Container>
      <LabelContent htmlFor={fieldId} id={fieldLabelId}>
        <TypeaheadSearchFieldLabel helpText={helpText} labelText={labelText} />
      </LabelContent>
      <DropdownMenu>
        <DropdownMenu.Trigger asChild>
          <DropdownButton
            aria-labelledby={fieldLabelId}
            hasError={isDefined(error)}
            id={fieldId}
            isDropdown
            placeholderText={placeholderText}
            trackingId={`${trackingIdPrefix}trigger`}
            valueText={value}
          />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align='start'>
          <DropdownMenu.Search
            handleClear={() => onSearch('')}
            handleSearch={onSearch}
            itemsToSearch={options}
            searchBarConfig={{
              placeholder: searchPlaceholder,
              trackingIdPrefix,
              visuallyHiddenLabel,
            }}
            searchKeys={searchKeys}
          />
          {isLoading && <EmptyState>Loading {resourceName}...</EmptyState>}
          {!isLoading && isTruthy(queryError) && (
            <EmptyState>
              Error loading {resourceName}. Please try again.
            </EmptyState>
          )}
          {!isLoading && !isTruthy(queryError) && options.length > 0 && (
            <DropdownMenu.RadioGroup onValueChange={onSelect} value={value}>
              {options.map((option) => (
                <DropdownMenu.RadioItem
                  key={option.externalID}
                  trackingId={`${trackingIdPrefix}-option`}
                  value={option.name}
                >
                  <DropdownMenu.ItemIndicator>
                    <DropdownMenu.IndicatorIcon />
                  </DropdownMenu.ItemIndicator>
                  {option.name}
                </DropdownMenu.RadioItem>
              ))}
            </DropdownMenu.RadioGroup>
          )}
          {!isLoading && !isTruthy(queryError) && options.length === 0 && (
            <EmptyState>
              {debouncedSearchTerm
                ? `No ${resourceName} found matching "${debouncedSearchTerm}"`
                : `No ${resourceName} available`}
            </EmptyState>
          )}
        </DropdownMenu.Content>
      </DropdownMenu>
      {isDefined(error) && (
        <ErrorWrapper>
          <ErrorIcon name='error' />
          <CaptionErrorText>{error.message}</CaptionErrorText>
        </ErrorWrapper>
      )}
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  width: 50%;
`;

const LabelContent = styled.label(
  ({ theme }) => `
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: ${theme.spacings[2]};
  `,
);

const EmptyState = styled.div(
  ({ theme }) => `
    padding: ${theme.spacings[3]}
      calc(${theme.spacings[2]} + ${theme.spacings[1]});
    color: ${theme.colors.gray5};
    text-align: center;
  `,
);

const ErrorWrapper = styled.div(
  ({ theme }) => `
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: ${theme.spacings[1]};
  `,
);

const ErrorIcon = styled(Icon)(
  ({ theme }) => `
    width: 1em;
    margin-right: ${theme.spacings[1]};
    color: ${theme.colors.aliases.error};
  `,
);
